import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import DescriptionIcon from "@material-ui/icons/Description";

import { useInvoiceStyles } from "../styles";

/**
 * The individual-invoice form: lot numbers, customer, buyer premium,
 * image option, add-ons, and the Create button.
 *
 * Presentational component.
 * Every value and callback comes from the parent.
 */
const CreateInvoiceForm = ({
  auctions,
  lotNumberData,
  onLotNumberChange,
  customerName,
  onCustomerNameChange,
  clientEmail,
  onClientEmailChange,
  buyerPremium,
  onBuyerPremiumChange,
  withImage,
  onWithImageChange,
  addons,
  onAddonChange,
  onAddAddon,
  onDeleteAddon,
  onSelectAuction,
  onCreate,
}) => {
  const classes = useInvoiceStyles();

  return (
    <>
      {/* ============================================================
          LOT NUMBERS
      ============================================================ */}

      <Grid item lg={4} md={5} xs={12}>
        <Card className={classes.sectionCard}>
          <CardHeader
            className={classes.cardHeader}
            avatar={<DescriptionIcon color="primary" />}
            title={
              <Typography className={classes.sectionTitle}>
                Lot Numbers
              </Typography>
            }
            subheader={
              <Typography className={classes.sectionSubtitle}>
                Enter the lot numbers to include in the invoice
              </Typography>
            }
          />

          <Divider />

          <CardContent className={classes.cardContent}>
            <TextField
              className={classes.lotInput}
              placeholder={"Example:\n1\n2\n3"}
              multiline
              rows={11}
              fullWidth
              variant="outlined"
              value={lotNumberData}
              onChange={(e) => onLotNumberChange(e.target.value)}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* ============================================================
          CREATE INVOICE
      ============================================================ */}

      <Grid item lg={8} md={7} xs={12}>
        <Card className={classes.sectionCard}>
          <CardHeader
            className={classes.cardHeader}
            title={
              <Typography className={classes.sectionTitle}>
                Create Invoice
              </Typography>
            }
            subheader={
              <Typography className={classes.sectionSubtitle}>
                Enter the customer and invoice details
              </Typography>
            }
          />

          <Divider />

          <CardContent className={classes.cardContent}>
            {/* ===================== EVENT ===================== */}

            <Autocomplete
              id="event-picker"
              getOptionLabel={(option) =>
                option ? option.auction_name : ""
              }
              onChange={onSelectAuction}
              options={auctions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={classes.field}
                  label="Select Event"
                  variant="outlined"
                  fullWidth
                />
              )}
            />

            {/* ===================== CUSTOMER DETAILS ===================== */}

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  className={classes.field}
                  fullWidth
                  margin="normal"
                  value={customerName}
                  label="Customer Name"
                  onChange={(e) =>
                    onCustomerNameChange(e.target.value)
                  }
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  className={classes.field}
                  fullWidth
                  margin="normal"
                  label="Email Address"
                  value={clientEmail}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) =>
                    onClientEmailChange(e.target.value)
                  }
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  className={classes.field}
                  fullWidth
                  margin="normal"
                  label="Buyer Premium (%)"
                  type="number"
                  value={buyerPremium}
                  onChange={(e) => {
                    let value = e.target.value;

                    // Allow empty while editing
                    if (value === "") {
                      onBuyerPremiumChange("");
                      return;
                    }

                    const numericValue = Number(value);

                    // Prevent negative values
                    if (numericValue < 0) {
                      value = "0";
                    }

                    // Prevent values above 100%
                    if (numericValue > 100) {
                      value = "100";
                    }

                    onBuyerPremiumChange(value);
                  }}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: 0,
                    max: 100,
                    step: "0.01",
                  }}
                />
              </Grid>
            </Grid>

            {/* ===================== INCLUDE IMAGE ===================== */}

            <Box className={classes.optionsBox}>
              <FormControlLabel
                className={classes.optionLabel}
                control={
                  <Checkbox
                    color="primary"
                    checked={withImage}
                    onChange={(event) =>
                      onWithImageChange(event.target.checked)
                    }
                    inputProps={{
                      "aria-label": "Include artwork images",
                    }}
                  />
                }
                label="Include artwork images"
              />

              <Typography className={classes.optionDescription}>
                Checked = artwork images will be included in the invoice.
                Unchecked = invoice will be generated without images.
              </Typography>
            </Box>

            {/* ===================== ADDONS / DEDUCTIONS ===================== */}

            <Box mt={3}>
              <Box className={classes.addonsHeader}>
                <Box>
                  <Typography className={classes.sectionTitle}>
                    Add-ons / Deductions
                  </Typography>

                  <Typography className={classes.sectionSubtitle}>
                    Add additional invoice adjustments
                  </Typography>
                </Box>

                <Button
                  className={classes.addButton}
                  size="small"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={onAddAddon}
                >
                  Add Row
                </Button>
              </Box>

              {addons.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Box key={index} className={classes.addonRow}>
                  <TextField
                    className={classes.descriptionField}
                    label="Description"
                    name="discription"
                    variant="outlined"
                    size="small"
                    value={item.discription}
                    onChange={(e) =>
                      onAddonChange(index, e)
                    }
                  />

                  <TextField
                    className={classes.priceField}
                    label="Price"
                    name="price"
                    type="number"
                    variant="outlined"
                    size="small"
                    value={item.price}
                    onChange={(e) =>
                      onAddonChange(index, e)
                    }
                  />

                  <IconButton
                    className={classes.deleteButton}
                    size="small"
                    onClick={() => onDeleteAddon(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </CardContent>

          <Divider />

          {/* ===================== FOOTER ===================== */}

          <Box className={classes.footer}>
            <Button
              className={classes.previewButton}
              color="primary"
              variant="contained"
              onClick={onCreate}
            >
              Create Invoice
            </Button>
          </Box>
        </Card>
      </Grid>
    </>
  );
};

CreateInvoiceForm.propTypes = {
  auctions: PropTypes.array,
  lotNumberData: PropTypes.string,
  onLotNumberChange: PropTypes.func.isRequired,

  customerName: PropTypes.string,
  onCustomerNameChange: PropTypes.func.isRequired,

  clientEmail: PropTypes.string,
  onClientEmailChange: PropTypes.func.isRequired,

  buyerPremium: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onBuyerPremiumChange: PropTypes.func.isRequired,

  withImage: PropTypes.bool,
  onWithImageChange: PropTypes.func.isRequired,

  addons: PropTypes.array,
  onAddonChange: PropTypes.func.isRequired,
  onAddAddon: PropTypes.func.isRequired,
  onDeleteAddon: PropTypes.func.isRequired,

  onSelectAuction: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

CreateInvoiceForm.defaultProps = {
  auctions: [],
  lotNumberData: "",
  customerName: "",
  clientEmail: "",
  buyerPremium: "0.00",
  withImage: true,
  addons: [],
};

export default CreateInvoiceForm;